import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import Reveal from "@/components/Reveal";
import { FAZZENDA_GALLERY, FAZZENDA_GALLERY_TABS } from "@/data/resorts";

export default function Gallery() {
    const [tab, setTab] = useState("todas");
    const [lightbox, setLightbox] = useState(null);

    const filtered = useMemo(
        () => (tab === "todas" ? FAZZENDA_GALLERY : FAZZENDA_GALLERY.filter((g) => g.cats.includes(tab))),
        [tab],
    );

    useEffect(() => {
        if (lightbox === null) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % filtered.length);
            if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + filtered.length) % filtered.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, filtered.length]);

    return (
        <section id="galeria" className="bg-[#F1ECE1] py-24 sm:py-32" data-testid="gallery-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-hover">Fotos & Destaques</p>
                    <h2 className="mt-3 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl lg:text-5xl">
                        Descubra o Fazzenda Park Resort
                    </h2>
                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-stone-600">
                        Uma prévia do que espera por você no Fazzenda Park Resort.
                    </p>
                </Reveal>

                <Reveal delay={0.1} className="mt-10">
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtros da galeria">
                        {FAZZENDA_GALLERY_TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={tab === t.id}
                                data-testid={`filter-tab-${t.id}`}
                                onClick={() => {
                                    setTab(t.id);
                                    setLightbox(null);
                                }}
                                className={`rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-200 active:scale-95 ${
                                    tab === t.id
                                        ? "bg-forest-deep text-white shadow-lg shadow-forest-deep/20"
                                        : "bg-white text-forest-deep hover:bg-forest-deep/5"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="mt-8 grid auto-rows-[180px] grid-cols-2 gap-4 sm:auto-rows-[220px] lg:grid-cols-4" data-testid="gallery-grid">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((item, idx) => (
                            <motion.button
                                layout
                                key={item.id}
                                type="button"
                                data-testid={`gallery-item-${item.id}`}
                                onClick={() => setLightbox(idx)}
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                                className={`group relative overflow-hidden rounded-2xl ${idx === 0 ? "col-span-2 row-span-2" : ""}`}
                            >
                                <img src={item.src} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                                <span className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/25" />
                                <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
                                    <Expand className="h-4 w-4" />
                                </span>
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            <AnimatePresence>
                {lightbox !== null && filtered[lightbox] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/92 p-4 backdrop-blur-sm"
                        onClick={() => setLightbox(null)}
                        data-testid="gallery-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Visualização ampliada da foto"
                    >
                        <button
                            type="button"
                            data-testid="lightbox-close-btn"
                            aria-label="Fechar"
                            onClick={() => setLightbox(null)}
                            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            data-testid="lightbox-prev-btn"
                            aria-label="Foto anterior"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox((v) => (v - 1 + filtered.length) % filtered.length);
                            }}
                            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 sm:left-6"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <motion.img
                            key={filtered[lightbox].id}
                            src={filtered[lightbox].src}
                            alt={filtered[lightbox].alt}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[82vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
                        />
                        <button
                            type="button"
                            data-testid="lightbox-next-btn"
                            aria-label="Próxima foto"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox((v) => (v + 1) % filtered.length);
                            }}
                            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 sm:right-6"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                        <p className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-5 py-2 text-xs text-white/90 backdrop-blur-md">
                            {filtered[lightbox].alt}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
